import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import EncycdataLogo from '@components/layout/EncycdataLogo';

interface AcademyContributionVideo {
  title: string;
  description?: string;
  videoUrl?: string;
  sponsorName?: string;
  sponsorLogoUrl?: string;
  code?: string;
  sport?: string;
  category?: string;
  sourceType?: string;
}

interface AcademyContestParticipant {
  id: string;
  contestTitle?: string;
  sport?: string;
  playerFirstName?: string;
  playerLastName?: string;
  playerIdCode?: string;
  ageCategory?: string;
  level?: string;
  status?: string;
  score?: number | null;
  videoUrl?: string;
  submittedAt?: string;
}

interface AcademyTournamentItem {
  id: string;
  sport?: string;
  title: string;
  description?: string;
  participationFormat?: string;
  showOnHomepage?: boolean;
  showOnRankingContest?: boolean;
}

interface PublicProfileResponse {
  academy: {
    academyIdCode: string;
    name: string;
    description?: string;
    country?: string;
    city?: string;
    academyType?: string;
  };
  sport: string;
  coaches: Array<{
    id: string;
    coachIdCode?: string;
    firstName?: string;
    lastName?: string;
    primarySport?: string;
    secondarySport?: string;
    coachLevel?: string;
  }>;
  students: Array<{
    id: string;
    playerIdCode?: string;
    firstName?: string;
    lastName?: string;
    ageCategory?: string;
    level?: string;
  }>;
  studentVideos: Array<{
    id: string;
    title: string;
    examType?: string;
    strokeCode?: string;
    videoUrl: string;
    status?: string;
    rating?: number | null;
    playerFirstName?: string;
    playerLastName?: string;
  }>;
  coachDemoVideos: Array<{
    type: 'skill' | 'drill';
    code?: string;
    title: string;
    subtitle?: string;
    videoUrl: string;
  }>;
  stats: {
    coachCount: number;
    studentCount: number;
    studentVideoCount: number;
    coachDemoCount: number;
    structuredTrainingVideoCount?: number;
    contestVideoCount?: number;
    learningVideoCount?: number;
    sponsorCount?: number;
    contestParticipantCount?: number;
    tournamentCount?: number;
  };
  contributions?: {
    structuredTrainingVideos?: AcademyContributionVideo[];
    contestVideos?: AcademyContributionVideo[];
    learningVideos?: AcademyContributionVideo[];
    sponsors?: Array<{ sponsorName?: string; sponsorLogoUrl?: string }>;
    contestParticipants?: AcademyContestParticipant[];
    tournaments?: AcademyTournamentItem[];
  };
}

const PublicAcademyProfilePage: React.FC = () => {
  const { academyCode = '' } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<PublicProfileResponse | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      if (!academyCode) {
        setError('Academy code is required');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/public/academy-profile/${encodeURIComponent(academyCode)}`);
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Failed to load academy profile');
        }
        setProfile(data.data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load academy profile');
      } finally {
        setLoading(false);
      }
    };
    void loadProfile();
  }, [academyCode]);

  const academyTitle = useMemo(() => {
    if (!profile?.academy) return 'Academy / School Profile';
    return `${profile.academy.name} (${profile.academy.academyIdCode})`;
  }, [profile]);

  const sportTheme = useMemo(() => {
    const sport = (profile?.sport || 'All Sports').toLowerCase();
    if (sport.includes('all')) return { color: '#0f4c81', bg: '#e0f2fe', gradient: 'linear-gradient(135deg, #0f4c81 0%, #7c3aed 55%, #f97316 100%)', icon: <EmojiEventsIcon /> };
    if (sport.includes('football')) return { color: '#2e7d32', bg: '#e8f5e9', gradient: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)', icon: <SportsSoccerIcon /> };
    if (sport.includes('cricket')) return { color: '#00695c', bg: '#e0f2f1', gradient: 'linear-gradient(135deg, #004d40 0%, #26a69a 100%)', icon: <SportsCricketIcon /> };
    if (sport.includes('hockey')) return { color: '#1565c0', bg: '#e3f2fd', gradient: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)', icon: <SportsHockeyIcon /> };
    if (sport.includes('athletics')) return { color: '#ef6c00', bg: '#fff3e0', gradient: 'linear-gradient(135deg, #e65100 0%, #ffb74d 100%)', icon: <FitnessCenterIcon /> };
    return { color: '#6a1b9a', bg: '#f3e5f5', gradient: 'linear-gradient(135deg, #4a148c 0%, #ab47bc 100%)', icon: <SportsTennisIcon /> };
  }, [profile?.sport]);

  const renderMetricCard = (label: string, value: number | undefined, icon: React.ReactNode, color: string, bg: string) => (
    <Grid item xs={6} sm={4} md={2}>
      <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: bg, border: `1px solid ${color}33`, height: '100%' }}>
        <Box sx={{ color, display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          {icon}
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1 }}>
            {value || 0}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: '#263238', fontWeight: 800 }}>
          {label}
        </Typography>
      </Box>
    </Grid>
  );

  const renderContributionVideos = (title: string, videos: AcademyContributionVideo[] = [], color: string, bg: string, icon: React.ReactNode) => (
    <Card sx={{ border: `1px solid ${color}33`, height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: bg, color, display: 'grid', placeItems: 'center' }}>
            {icon}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color }}>
            {title} ({videos.length})
          </Typography>
        </Stack>
        {videos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No published contributions yet.</Typography>
        ) : (
          <Grid container spacing={1.2}>
            {videos.slice(0, 6).map((video, index) => (
              <Grid item xs={12} sm={6} key={`${title}-${video.code || index}-${video.videoUrl || index}`}>
                <Box sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1.5, height: '100%', bgcolor: '#fff' }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {video.title || video.code || 'Learning Video'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {[video.sport, video.category, video.code, video.description].filter(Boolean).join(' | ')}
                  </Typography>
                  {video.videoUrl ? (
                    <Button size="small" startIcon={<SmartDisplayIcon sx={{ fontSize: 14 }} />} onClick={() => window.open(video.videoUrl, '_blank', 'noopener,noreferrer')} sx={{ textTransform: 'none', px: 0 }}>
                      Watch
                    </Button>
                  ) : null}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );

  const renderTournamentItems = (items: AcademyTournamentItem[] = []) => (
    <Card sx={{ border: '1px solid #bfdbfe' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <EmojiEventsIcon sx={{ color: '#1d4ed8' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#1d4ed8' }}>
            Tournaments / Contest Items Arranged ({items.length})
          </Typography>
        </Stack>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No tournament or contest items published yet.</Typography>
        ) : (
          <Grid container spacing={1.2}>
            {items.slice(0, 12).map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Box sx={{ p: 1.15, border: '1px solid #bfdbfe', borderRadius: 1.5, bgcolor: '#eff6ff', height: '100%' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" label={item.sport || 'Sport'} sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 800 }} />
                    {item.participationFormat ? (
                      <Chip size="small" variant="outlined" label={item.participationFormat} sx={{ fontWeight: 700 }} />
                    ) : null}
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {item.description || 'Tournament or challenge item published for this sport.'}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 3 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EncycdataLogo size={36} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f4c81' }}>
              Academy / School Public Profile
            </Typography>
          </Box>
          <Button component={RouterLink} to="/" variant="outlined" sx={{ textTransform: 'none' }}>
            Back to Home
          </Button>
        </Stack>

        {loading ? (
          <Card>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 1.5 }}>Loading public profile...</Typography>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent>
              <Typography color="error" sx={{ fontWeight: 700 }}>
                {error}
              </Typography>
            </CardContent>
          </Card>
        ) : profile ? (
          <Stack spacing={2}>
            <Card sx={{ overflow: 'hidden', border: '1px solid #dbe7f3' }}>
              <Box sx={{ p: { xs: 2, md: 3 }, background: sportTheme.gradient, color: '#fff' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.18)', display: 'grid', placeItems: 'center' }}>
                        {sportTheme.icon}
                      </Box>
                      <Chip label={profile.sport} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 800 }} />
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                      {academyTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, maxWidth: 760, color: 'rgba(255,255,255,0.9)' }}>
                      {profile.academy.description || 'Public profile showing academy contributions to structured sports learning, contest videos, tournament items, and student contest participation across sports.'}
                    </Typography>
                  </Box>
                  {(profile.academy.city || profile.academy.country) && (
                    <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.14)' }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: 'rgba(255,255,255,0.75)' }}>Location</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {`${profile.academy.city || ''}${profile.academy.city && profile.academy.country ? ', ' : ''}${profile.academy.country || ''}`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
              <CardContent>
                <Grid container spacing={1.25}>
                  {renderMetricCard('Training Videos', profile.stats.structuredTrainingVideoCount, <SchoolIcon fontSize="small" />, '#0f766e', '#ccfbf1')}
                  {renderMetricCard('Contest Videos', profile.stats.contestVideoCount, <EmojiEventsIcon fontSize="small" />, '#c2410c', '#ffedd5')}
                  {renderMetricCard('Other Learning', profile.stats.learningVideoCount, <SmartDisplayIcon fontSize="small" />, sportTheme.color, sportTheme.bg)}
                  {renderMetricCard('Tournaments', profile.stats.tournamentCount, <EmojiEventsIcon fontSize="small" />, '#1d4ed8', '#dbeafe')}
                  {renderMetricCard('Contest Students', profile.stats.contestParticipantCount, <FitnessCenterIcon fontSize="small" />, '#7c2d12', '#ffedd5')}
                </Grid>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {renderContributionVideos('Structured Training Videos', profile.contributions?.structuredTrainingVideos || [], '#0f766e', '#ccfbf1', <SchoolIcon fontSize="small" />)}
              </Grid>
              <Grid item xs={12} md={4}>
                {renderContributionVideos('Contest Videos', profile.contributions?.contestVideos || [], '#c2410c', '#ffedd5', <EmojiEventsIcon fontSize="small" />)}
              </Grid>
              <Grid item xs={12} md={4}>
                {renderContributionVideos('Other Learning Videos', profile.contributions?.learningVideos || [], sportTheme.color, sportTheme.bg, <SmartDisplayIcon fontSize="small" />)}
              </Grid>
            </Grid>

            {renderTournamentItems(profile.contributions?.tournaments || [])}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card sx={{ border: '1px solid #fed7aa', height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <EmojiEventsIcon sx={{ color: '#c2410c' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#c2410c' }}>
                        Students in Contests ({profile.contributions?.contestParticipants?.length || 0})
                      </Typography>
                    </Stack>
                    {(!profile.contributions?.contestParticipants || profile.contributions.contestParticipants.length === 0) ? (
                      <Typography variant="body2" color="text.secondary">No contest participation published yet.</Typography>
                    ) : (
                      <Grid container spacing={1}>
                        {profile.contributions.contestParticipants.slice(0, 8).map((entry) => (
                          <Grid item xs={12} sm={6} key={entry.id}>
                            <Box sx={{ p: 1, border: '1px solid #fed7aa', borderRadius: 1.5, bgcolor: '#fff' }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {[entry.playerFirstName, entry.playerLastName].filter(Boolean).join(' ') || entry.playerIdCode || 'Student'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {[entry.sport, entry.contestTitle, entry.ageCategory, entry.level, entry.status].filter(Boolean).join(' | ')}
                              </Typography>
                              {entry.videoUrl ? (
                                <Button size="small" startIcon={<SmartDisplayIcon sx={{ fontSize: 14 }} />} onClick={() => window.open(entry.videoUrl, '_blank', 'noopener,noreferrer')} sx={{ textTransform: 'none', px: 0 }}>
                                  Watch Submission
                                </Button>
                              ) : null}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        ) : null}
      </Container>
    </Box>
  );
};

export default PublicAcademyProfilePage;
